<script lang="ts">
import { defineComponent, h, type VNodeChild, type PropType } from 'vue';
import type { TiptapDoc } from '../types/entities';
import type { CorrectionEdit } from '../types/correction';

type Node = { type: string; text?: string; attrs?: Record<string, unknown>; marks?: Array<{ type: string }>; content?: Node[] };
export default defineComponent({
  props: { document: { type: Object as PropType<TiptapDoc>, required: true }, edits: { type: Array as PropType<CorrectionEdit[]>, required: true }, showRemoved: Boolean },
  setup(props) {
    return () => {
      let nodeIndex = 0;
      function render(node: Node): VNodeChild {
        if (node.type === 'text') {
          const index = nodeIndex++; const text = node.text ?? ''; const children: VNodeChild[] = []; let cursor = 0;
          for (const edit of props.edits.filter((item) => item.nodeIndex === index)) {
            children.push(text.slice(cursor, edit.from));
            if (props.showRemoved || !edit.after) children.push(h('del', { class: 'correction-removed', title: edit.reason }, edit.before));
            if (edit.after) children.push(h('ins', { class: 'correction-added', title: edit.reason }, edit.after));
            cursor = edit.to;
          }
          children.push(text.slice(cursor));
          let result: VNodeChild = h('span', children);
          for (const mark of node.marks ?? []) {
            const tag = ({ bold: 'strong', italic: 'em', underline: 'u', strike: 's', code: 'code' } as Record<string, string>)[mark.type];
            if (tag) result = h(tag, [result]);
          }
          return result;
        }
        if (node.type === 'entityMention') return h('span', { class: 'correction-mention' }, String(node.attrs?.label ?? 'Verknüpfung'));
        if (node.type === 'hardBreak') return h('br');
        if (node.type === 'horizontalRule') return h('hr');
        if (node.type === 'image') return h('span', '[Bild unverändert]');
        const tag = ({ doc: 'div', paragraph: 'p', heading: `h${Math.min(6, Math.max(1, Number(node.attrs?.level) || 2))}`, bulletList: 'ul', orderedList: 'ol', listItem: 'li', blockquote: 'blockquote', codeBlock: 'pre', table: 'table', tableRow: 'tr', tableHeader: 'th', tableCell: 'td' } as Record<string, string>)[node.type] ?? 'div';
        return h(tag, (node.content ?? []).map(render));
      }
      return h('div', { class: 'correction-preview-text' }, [render(props.document as unknown as Node)]);
    };
  }
});
</script>

<style>
.correction-preview-text { white-space:pre-wrap; overflow-wrap:anywhere; line-height:1.8; padding:18px; border:1px solid var(--ui-border); border-radius:10px; background:var(--ui-surface-1); color:var(--ui-text); }
.correction-preview-text p { margin:0 0 1em; }
.correction-preview-text p:last-child { margin-bottom:0; }
.correction-removed,.correction-added { color:var(--ui-danger); background:var(--ui-danger-surface); }
.correction-removed { text-decoration:line-through; opacity:.8; }
.correction-added { text-decoration:underline; text-underline-offset:3px; font-weight:650; }
.correction-mention { color:var(--ui-accent); }
</style>
